import { AnimatePresence, motion } from "framer-motion"
import { Download, Expand, QrCode, X } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import QRCode from "qrcode"
import { buildStorefrontShortUrl } from "../../utils/storefront"
import { isCarStore } from "../../utils/storeCategory"

type StorefrontLiveQrToolsProps = {
  store: any
  className?: string
}

const OVERLAY_WIDTH = 1920
const OVERLAY_HEIGHT = 1080

export default function StorefrontLiveQrTools({
  store,
  className = "",
}: StorefrontLiveQrToolsProps) {
  const [qrDataUrl, setQrDataUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false)

  const storefrontShortUrl = buildStorefrontShortUrl(store)
  const storeName = String(store?.name || "Boutique Gestock").trim()
  const carStore = isCarStore(store)
  const commerceWhatsappNumber = sanitizeWhatsappNumber(store?.publicOrderWhatsappNumber)
  const commerceWhatsappLabel = commerceWhatsappNumber
    ? `WhatsApp commerce: +${commerceWhatsappNumber}`
    : "WhatsApp commerce non renseigne"

  const liveCaption = useMemo(() => {
    return carStore
      ? `Scannez pour ouvrir le parc auto ${storeName}`
      : `Scannez pour ouvrir la boutique ${storeName}`
  }, [carStore, storeName])

  useEffect(() => {
    if (!storefrontShortUrl) {
      setQrDataUrl("")
      return
    }

    let cancelled = false

    const generateQr = async () => {
      setLoading(true)

      try {
        const url = await QRCode.toDataURL(storefrontShortUrl, {
          margin: 1,
          width: 720,
          color: {
            dark: "#0f172a",
            light: "#ffffff",
          },
        })

        if (!cancelled) {
          setQrDataUrl(url)
        }
      } catch (error) {
        console.error(error)
        if (!cancelled) {
          setQrDataUrl("")
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    generateQr()

    return () => {
      cancelled = true
    }
  }, [storefrontShortUrl])

  const downloadQrPng = () => {
    if (!qrDataUrl) return
    triggerDownload(qrDataUrl, `gestock-live-qr-${slugify(storeName)}.png`)
  }

  const downloadOverlayPng = async () => {
    if (!qrDataUrl) return

    const canvas = document.createElement("canvas")
    canvas.width = OVERLAY_WIDTH
    canvas.height = OVERLAY_HEIGHT
    const context = canvas.getContext("2d")

    if (!context) return

    context.fillStyle = "#ecfeff"
    context.fillRect(0, 0, OVERLAY_WIDTH, OVERLAY_HEIGHT)

    const gradient = context.createLinearGradient(0, 0, OVERLAY_WIDTH, OVERLAY_HEIGHT)
    gradient.addColorStop(0, "#082f49")
    gradient.addColorStop(0.55, "#0369a1")
    gradient.addColorStop(1, "#0891b2")
    context.fillStyle = gradient
    roundRect(context, 60, 80, 1800, 920, 56)
    context.fill()

    context.fillStyle = "rgba(255,255,255,0.12)"
    roundRect(context, 120, 132, 280, 64, 30)
    context.fill()

    context.fillStyle = "#e0f2fe"
    context.font = "700 30px Poppins, sans-serif"
    context.fillText("LIVE SHOPPING", 152, 176)

    context.fillStyle = "#ffffff"
    context.font = "700 88px Poppins, sans-serif"
    wrapText(context, storeName, 120, 315, 900, 98)

    context.fillStyle = "rgba(255,255,255,0.8)"
    context.font = "400 40px Poppins, sans-serif"
    wrapText(context, liveCaption, 120, 460, 860, 56)

    context.fillStyle = "rgba(255,255,255,0.14)"
    roundRect(context, 120, 610, 900, 210, 40)
    context.fill()

    context.fillStyle = "#dbeafe"
    context.font = "600 28px Poppins, sans-serif"
    context.fillText("Lien court", 164, 676)

    context.fillStyle = "#ffffff"
    context.font = "700 34px Poppins, sans-serif"
    wrapText(context, storefrontShortUrl, 164, 735, 790, 44)

    context.fillStyle = "#ecfeff"
    context.font = "500 32px Poppins, sans-serif"
    context.fillText("Scannez avec votre téléphone pour commander", 164, 878)

    context.fillStyle = "#ffffff"
    roundRect(context, 1150, 160, 560, 560, 44)
    context.fill()

    const qrImage = await loadImage(qrDataUrl)
    context.drawImage(qrImage, 1210, 220, 440, 440)

    context.fillStyle = "rgba(255,255,255,0.12)"
    roundRect(context, 1080, 770, 700, 150, 34)
    context.fill()

    context.fillStyle = "#ffffff"
    context.font = "700 40px Poppins, sans-serif"
    context.textAlign = "center"
    context.fillText("Ouvrez toute la boutique", 1430, 835)
    context.fillStyle = "#dbeafe"
    context.font = "500 28px Poppins, sans-serif"
    context.fillText("Tous les produits actifs en un scan", 1430, 885)
    context.textAlign = "start"

    context.fillStyle = "rgba(255,255,255,0.16)"
    roundRect(context, 120, 840, 900, 92, 28)
    context.fill()

    context.fillStyle = "#ffffff"
    context.font = "600 30px Poppins, sans-serif"
    wrapText(context, commerceWhatsappLabel, 164, 896, 800, 38)

    triggerDownload(
      canvas.toDataURL("image/png"),
      `gestock-live-overlay-${slugify(storeName)}.png`
    )
  }

  const downloadTransparentOverlayPng = async () => {
    if (!qrDataUrl) return

    const canvas = document.createElement("canvas")
    canvas.width = 1400
    canvas.height = 1400
    const context = canvas.getContext("2d")

    if (!context) return

    context.clearRect(0, 0, canvas.width, canvas.height)

    const qrPanelX = 210
    const qrPanelY = 80
    const qrPanelSize = 980

    context.fillStyle = "rgba(15,23,42,0.78)"
    roundRect(context, 70, 30, 1260, 1320, 56)
    context.fill()

    context.fillStyle = "#ffffff"
    roundRect(context, qrPanelX, qrPanelY, qrPanelSize, qrPanelSize, 52)
    context.fill()

    const qrImage = await loadImage(qrDataUrl)
    context.drawImage(qrImage, qrPanelX + 90, qrPanelY + 90, 800, 800)

    context.fillStyle = "#e0f2fe"
    context.font = "700 34px Poppins, sans-serif"
    context.textAlign = "center"
    context.fillText("SCANEZ POUR COMMANDER", canvas.width / 2, 1010)

    context.fillStyle = "#ffffff"
    context.font = "700 42px Poppins, sans-serif"
    wrapCenteredText(context, storefrontShortUrl, canvas.width / 2, 1095, 1040, 54)

    context.fillStyle = "#dbeafe"
    context.font = "600 34px Poppins, sans-serif"
    wrapCenteredText(context, commerceWhatsappLabel, canvas.width / 2, 1230, 980, 44)
    context.textAlign = "start"

    triggerDownload(
      canvas.toDataURL("image/png"),
      `gestock-live-overlay-transparent-${slugify(storeName)}.png`
    )
  }

  if (!storefrontShortUrl) return null

  return (
    <>
      <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
            <QrCode size={20} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Kit LIVE QR</p>
            <p className="mt-1 text-xs text-slate-500">
              {carStore
                ? "Affichez ce QR pendant un live pour envoyer les viewers vers tout votre parc auto public."
                : "Affichez ce QR pendant un live pour envoyer les viewers vers toute votre vitrine produits."}
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#f8fafc,#eff6ff)] p-4">
          <div className="flex items-center justify-center rounded-2xl bg-white p-4 shadow-sm">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt={`QR ${storeName}`} className="h-56 w-56 rounded-xl object-contain sm:h-64 sm:w-64" />
            ) : (
              <div className="flex h-56 w-56 items-center justify-center rounded-xl bg-slate-100 text-sm text-slate-400 sm:h-64 sm:w-64">
                {loading ? "Chargement..." : "QR indisponible"}
              </div>
            )}
          </div>
          <p className="mt-4 text-center text-sm font-semibold text-slate-800">{storeName}</p>
          <p className="mt-3 break-all text-center text-sm font-semibold text-slate-700">{storefrontShortUrl}</p>
          <p className="mt-2 text-center text-xs font-medium text-slate-500">{commerceWhatsappLabel}</p>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setIsFullscreenOpen(true)}
            disabled={!qrDataUrl}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Expand size={16} />
            Fullscreen QR
          </button>

          <button
            type="button"
            onClick={downloadQrPng}
            disabled={!qrDataUrl}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={16} />
            Télécharger le QR
          </button>

          <button
            type="button"
            onClick={downloadOverlayPng}
            disabled={!qrDataUrl}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={16} />
            Télécharger l'overlay LIVE
          </button>

          <button
            type="button"
            onClick={downloadTransparentOverlayPng}
            disabled={!qrDataUrl}
            className="inline-flex items-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2.5 text-sm font-semibold text-cyan-800 transition hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download size={16} />
            Overlay transparent PNG
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isFullscreenOpen && qrDataUrl && (
          <motion.div
            className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative flex h-full max-h-[92vh] w-full max-w-3xl flex-col items-center justify-center rounded-[2rem] border border-white/20 bg-[linear-gradient(180deg,#082f49,#0f172a)] p-6 text-white shadow-2xl"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
            >
              <button
                type="button"
                onClick={() => setIsFullscreenOpen(false)}
                className="absolute right-5 top-5 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white transition hover:bg-white/20"
              >
                <X size={18} />
              </button>

              <p className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-100">
                Live shopping
              </p>
              <h2 className="mt-5 text-center text-3xl font-bold sm:text-4xl">{storeName}</h2>
              <p className="mt-3 text-center text-sm text-white/75 sm:text-base">
                Scannez ce code pour ouvrir la boutique et commander en direct.
              </p>

              <div className="mt-8 rounded-[2rem] bg-white p-5 shadow-xl sm:p-7">
                <img src={qrDataUrl} alt={`QR ${storeName}`} className="h-80 w-80 rounded-2xl object-contain sm:h-[28rem] sm:w-[28rem]" />
              </div>

              <p className="mt-6 max-w-2xl break-all text-center text-base font-semibold text-cyan-100 sm:text-lg">
                {storefrontShortUrl}
              </p>
              <p className="mt-3 text-center text-sm font-medium text-white/80 sm:text-base">
                {commerceWhatsappLabel}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function triggerDownload(dataUrl: string, filename: string) {
  const link = document.createElement("a")
  link.href = dataUrl
  link.download = filename
  link.click()
}

function slugify(value: string) {
  return String(value || "store")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function sanitizeWhatsappNumber(value: any) {
  return String(value || "").replace(/[^\d]/g, "")
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
  })
}

function wrapText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) {
  const words = String(text || "").split(" ")
  let line = ""
  let currentY = y

  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word
    const width = context.measureText(testLine).width

    if (width > maxWidth && line) {
      context.fillText(line, x, currentY)
      line = word
      currentY += lineHeight
    } else {
      line = testLine
    }
  }

  if (line) {
    context.fillText(line, x, currentY)
  }
}

function wrapCenteredText(
  context: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) {
  const words = String(text || "").split(" ")
  let line = ""
  let currentY = y

  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word
    const width = context.measureText(testLine).width

    if (width > maxWidth && line) {
      context.fillText(line, centerX, currentY)
      line = word
      currentY += lineHeight
    } else {
      line = testLine
    }
  }

  if (line) {
    context.fillText(line, centerX, currentY)
  }
}

function roundRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  context.beginPath()
  context.moveTo(x + radius, y)
  context.lineTo(x + width - radius, y)
  context.quadraticCurveTo(x + width, y, x + width, y + radius)
  context.lineTo(x + width, y + height - radius)
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  context.lineTo(x + radius, y + height)
  context.quadraticCurveTo(x, y + height, x, y + height - radius)
  context.lineTo(x, y + radius)
  context.quadraticCurveTo(x, y, x + radius, y)
  context.closePath()
}
